import * as Form from '@radix-ui/react-form';
import { Button, Text, useTheme } from '@querodelivery/uni-react';
import {
  format,
  getStartOfDay,
  isSameHour,
  isValidDate,
  toDate,
} from '@querodelivery/qd-date-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination } from '@quero-delivery/quero-components-web';
import { toast } from 'react-toastify';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { Table, useStackedTableLayout } from '@/_components/table';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';
import { useCities } from '@/_hooks/use-cities';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { setFilterCityOnMissionsList } from '@/_store/modules/filters/slice';
import { objectNotNullV2, stringifyQueryStringArray } from '@/Utils/FnUtils';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { colors } from '@/Themes/Colors';
import { useGetMissions } from '../../api/get-missions';
import { useGetMissionById } from '../../api/get-mission-by-id';
import { usePutCancelMission } from '../../api/put-cancel-mission';
import {
  EMissionStatus,
  MISSION_STATUS_OPTIONS,
  MISSIONS_LIST_FORM_DEFAULT_VALUES,
  MISSIONS_LIST_FORM_SCHEMA,
  TRANSLATE_MISSION_STATUS,
} from '../../constants';
import type {
  CreateMissionFormType,
  GetMissionsListParams,
  Mission,
  MissionsListFormType,
} from '../../types';
import { mapMissionByManagerToFormValues } from '../../utils/map-mission-by-manager-to-form';
import { CancelMissionModal } from '../cancel-mission-modal/cancel-mission-modal';
import { ConfirmMissionModal } from '../confirm-mission-modal/confirm-mission-modal';
import { CreateMissionModal } from '../create-mission-modal/create-mission-modal';
import styles from './missions-list.module.scss';

const limit = DEFAULT_LIMIT_REQUEST;
const MISSIONS_TABLE_GRID_COLUMNS = 'repeat(4, minmax(0, 1fr))';
const MISSIONS_TABLE_COLUMN_COUNT = 5;

export const MissionsList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateMissionOpen, setIsCreateMissionOpen] = useState(false);
  const [isCancelMissionOpen, setIsCancelMissionOpen] = useState(false);
  const [viewMissionId, setViewMissionId] = useState<string | null>(null);
  const [editMission, setEditMission] = useState<{
    id: string;
    values: CreateMissionFormType;
  } | null>(null);
  const { color } = useTheme();

  const isStackedTableLayout = useStackedTableLayout();

  const filteredCityId = useAppSelector(
    state => state.filters.cityIdOnMissionsList,
  );

  const { CITIES_OPTIONS, loadingCities } = useCities();

  useEffect(() => {
    if (filteredCityId) {
      setSearchParams(prev => {
        prev.set('cityId', filteredCityId);
        return prev;
      });
    }
  }, [filteredCityId]);

  const initialParams: GetMissionsListParams = {
    cityId: filteredCityId || searchParams.get('cityId') || undefined,
    missionName: searchParams.get('missionName') || undefined,
    status: (searchParams.get('status') as EMissionStatus | null) || undefined,
    startDate:
      searchParams.get('startDate') &&
      isValidDate(new Date(searchParams.get('startDate') as string))
        ? toDate(searchParams.get('startDate') as string)
        : undefined,
    endDate:
      searchParams.get('endDate') &&
      isValidDate(new Date(searchParams.get('endDate') as string))
        ? toDate(searchParams.get('endDate') as string)
        : undefined,
  };

  const [page, setPage] = useState(1);
  const [params, setParams] = useState<GetMissionsListParams>(initialParams);

  const {
    data: missions,
    isLoading,
    isError,
  } = useGetMissions({
    page,
    limit,
    ...params,
  });

  const {
    data: pendingMission,
    isLoading: isLoadingPendingMission,
    isError: isPendingMissionError,
  } = useGetMissionById(
    { missionId: viewMissionId || '' },
    { enabled: !!viewMissionId },
  );

  const { mutate: cancelMission, isLoading: isCancelingMission } =
    usePutCancelMission();

  useEffect(() => {
    if (!viewMissionId || !isPendingMissionError) return;

    toast.error('Não foi possível carregar os detalhes da missão.');
    setViewMissionId(null);
  }, [viewMissionId, isPendingMissionError]);

  const pendingMissionFormValues = pendingMission
    ? mapMissionByManagerToFormValues(pendingMission)
    : null;
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<MissionsListFormType>({
    resolver: yupResolver(MISSIONS_LIST_FORM_SCHEMA),
    defaultValues: {
      ...MISSIONS_LIST_FORM_DEFAULT_VALUES,
      cityId: initialParams.cityId || '',
      missionName: initialParams.missionName || '',
      status: initialParams.status,
      startDate: initialParams.startDate,
      endDate: initialParams.endDate,
    },
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const buildMissionsParams = (
    data: MissionsListFormType,
    currentPage: number,
  ): GetMissionsListParams => {
    return objectNotNullV2({
      cityId: data.cityId || undefined,
      missionName: data.missionName || undefined,
      status: (data.status as EMissionStatus | undefined) || undefined,
      startDate: data.startDate || undefined,
      endDate: data.endDate || undefined,
      page: currentPage,
      limit,
    });
  };

  const onSubmit: SubmitHandler<MissionsListFormType> = data => {
    const queries = buildMissionsParams(data, 1);

    setPage(1);
    setParams(queries);
    setSearchParams(
      stringifyQueryStringArray({
        ...queries,
        startDate: data.startDate?.toISOString() || '',
        endDate: data.endDate?.toISOString() || '',
        page: undefined,
        limit: undefined,
      }),
    );
  };

  const handleChangeDate = (
    newDate: Date | null,
    onChange: (...event: unknown[]) => void,
  ) => {
    if (!newDate) {
      return onChange(undefined);
    }

    if (isSameHour(newDate, getStartOfDay(newDate))) {
      return onChange(getStartOfDay(newDate));
    }

    onChange(newDate);
  };

  function getStatusColorsByStatus(status?: EMissionStatus): {
    innerColor?: string;
    outerColor?: string;
  } {
    switch (status) {
      case EMissionStatus.PENDING:
        return {
          innerColor: color['color-warning-fill'],
          outerColor: color['color-warning-fill-disable'],
        };
      case EMissionStatus.ACTIVE:
        return {
          innerColor: color['color-success-fill'],
          outerColor: color['color-success-fill-disable'],
        };
      case EMissionStatus.FINISHED:
        return {
          innerColor: color['color-danger-fill'],
          outerColor: color['color-danger-surface'],
        };
      case EMissionStatus.CANCELED:
      default:
        return {
          innerColor: color['color-base-border-2'],
          outerColor: colors.gray70,
        };
    }
  }

  function renderStatusBadge(mission: Mission) {
    return (
      <div className={styles.statusContainer}>
        <div
          className={styles.statusOuterCircle}
          style={{
            backgroundColor: getStatusColorsByStatus(mission.status).outerColor,
          }}
        >
          <div
            className={styles.statusInnerCircle}
            style={{
              backgroundColor: getStatusColorsByStatus(mission.status)
                .innerColor,
            }}
          />
        </div>
        <Text variant="bodySm" tone="supporting" fontWeight="bold">
          {TRANSLATE_MISSION_STATUS[mission.status]}
        </Text>
      </div>
    );
  }

  const handleClickOnMissionRow = (
    missionId: string,
    missionStatus: EMissionStatus,
  ) => {
    if (missionStatus === EMissionStatus.PENDING) {
      setViewMissionId(missionId);
      return;
    }

    navigate(`/missoes/${missionId}`);
  };

  const handleCloseViewMission = () => {
    setIsCancelMissionOpen(false);
    setViewMissionId(null);
  };

  const handleOpenCancelMission = () => {
    if (!viewMissionId || !pendingMissionFormValues) return;

    setIsCancelMissionOpen(true);
  };

  const handleCloseCancelMission = () => {
    setIsCancelMissionOpen(false);
  };

  const handleConfirmCancelMission = () => {
    if (!viewMissionId) return;

    cancelMission(
      { missionId: viewMissionId },
      {
        onSuccess: handleCloseViewMission,
      },
    );
  };

  const handleOpenCreateMission = () => {
    setEditMission(null);
    setIsCreateMissionOpen(true);
  };

  const handleCloseCreateMission = () => {
    setIsCreateMissionOpen(false);
    setEditMission(null);
  };

  const handleEditScheduledMission = () => {
    if (!viewMissionId || !pendingMissionFormValues) return;

    setEditMission({
      id: viewMissionId,
      values: pendingMissionFormValues,
    });
    setIsCancelMissionOpen(false);
    setViewMissionId(null);
    setIsCreateMissionOpen(true);
  };

  function handleChangePage(newPage: number) {
    setPage(newPage);
    setParams(buildMissionsParams(getValues(), newPage));
  }

  function renderMissionRow(mission: Mission) {
    return (
      <Table.Row
        key={mission._id}
        gridTemplateColumns={MISSIONS_TABLE_GRID_COLUMNS}
        onClick={() => handleClickOnMissionRow(mission._id, mission.status)}
      >
        <Table.Cell>
          <Text>{mission.missionName}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {mission.startDate
              ? format(new Date(mission.startDate), 'dd/MM/yyyy HH:mm')
              : '- - -'}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {mission.endDate
              ? format(new Date(mission.endDate), 'dd/MM/yyyy HH:mm')
              : '- - -'}
          </Text>
        </Table.Cell>
        <Table.Cell>{renderStatusBadge(mission)}</Table.Cell>
      </Table.Row>
    );
  }

  function renderMissionMobileCard(mission: Mission) {
    return (
      <Table.MobileCard
        key={mission._id}
        onClick={() => handleClickOnMissionRow(mission._id, mission.status)}
      >
        <Table.MobileItem label="Missão">
          <Text fontWeight="semibold" tone="supporting">
            {mission.missionName}
          </Text>
        </Table.MobileItem>
        <Table.MobileItem label="Data de início">
          {mission.startDate
            ? format(new Date(mission.startDate), 'dd/MM/yyyy HH:mm')
            : '- - -'}
        </Table.MobileItem>
        <Table.MobileItem label="Data de finalização">
          {mission.endDate
            ? format(new Date(mission.endDate), 'dd/MM/yyyy HH:mm')
            : '- - -'}
        </Table.MobileItem>
        <Table.MobileItem label="Status">
          {renderStatusBadge(mission)}
        </Table.MobileItem>
      </Table.MobileCard>
    );
  }

  return (
    <div className="container">
      <Form.Root
        className={styles.filtersForm}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.filtersRow}>
          <Controller
            name="cityId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                search
                label="Buscar município"
                options={CITIES_OPTIONS}
                loading={loadingCities}
                value={value}
                placeholder="buscar município"
                onChange={selectedValue => {
                  if (typeof selectedValue === 'string') {
                    dispatch(setFilterCityOnMissionsList(selectedValue));
                  }
                  onChange(selectedValue);
                }}
                errorMessage={errors.cityId?.message}
                error={!!errors.cityId?.message}
              />
            )}
          />

          <Controller
            name="missionName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="missionName"
                value={value}
                label="Buscar missão"
                placeholder="buscar missão"
                onChange={onChange}
                errorMessage={errors.missionName?.message}
                error={!!errors.missionName?.message}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Status"
                options={MISSION_STATUS_OPTIONS}
                value={value || ''}
                placeholder="Selecione o status"
                onChange={onChange}
                errorMessage={errors.status?.message}
                error={!!errors.status?.message}
              />
            )}
          />
        </div>

        <div className={styles.filtersBottomRow}>
          <div className={styles.datesRow}>
            <Controller
              name="startDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className={styles.dateField}>
                  <CalendarInput
                    name="startDate"
                    value={value}
                    label="Data inicial"
                    selectsStart
                    startDate={value || undefined}
                    endDate={endDate || undefined}
                    onChange={(newDate: Date | null) =>
                      handleChangeDate(newDate, onChange)
                    }
                    withPortal={isStackedTableLayout}
                    clearable
                    popperPlacement="top"
                  />
                  {errors.startDate ? (
                    <span className="text-error">
                      {errors.startDate.message}
                    </span>
                  ) : null}
                </div>
              )}
            />

            <Controller
              name="endDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className={styles.dateField}>
                  <CalendarInput
                    name="endDate"
                    value={value}
                    label="Data final"
                    selectsEnd
                    minDate={startDate || undefined}
                    startDate={startDate || undefined}
                    endDate={value || undefined}
                    onChange={(newDate: Date | null) =>
                      handleChangeDate(newDate, onChange)
                    }
                    withPortal={isStackedTableLayout}
                    clearable
                    popperPlacement="top"
                  />
                  {errors.endDate ? (
                    <span className="text-error">{errors.endDate.message}</span>
                  ) : null}
                </div>
              )}
            />
          </div>

          <div className={styles.actionsRow}>
            <Button
              variant="outline"
              size="lg"
              type="submit"
              width={isStackedTableLayout ? '100%' : '100px'}
            >
              Buscar
            </Button>
            <Button
              variant="cta"
              size="lg"
              type="button"
              icon={<FontAwesomeIcon icon={faPlus} />}
              iconPosition="left"
              onClick={handleOpenCreateMission}
              width={isStackedTableLayout ? '100%' : '200px'}
            >
              criar missão
            </Button>
          </div>
        </div>
      </Form.Root>

      <CreateMissionModal
        isOpen={isCreateMissionOpen}
        missionId={editMission?.id}
        defaultValues={editMission?.values}
        onClose={handleCloseCreateMission}
      />

      <ConfirmMissionModal
        mode="view"
        isOpen={!!viewMissionId}
        formValues={pendingMissionFormValues}
        isLoading={isLoadingPendingMission || isCancelingMission}
        creatorName={pendingMission?.manager.name}
        totalDeliverymen={pendingMission?.totalDeliverymen}
        onClose={handleCloseViewMission}
        onEdit={handleEditScheduledMission}
        onCancelMission={handleOpenCancelMission}
      />

      <CancelMissionModal
        isOpen={isCancelMissionOpen}
        isLoading={isCancelingMission}
        onConfirm={handleConfirmCancelMission}
        onClose={handleCloseCancelMission}
      />

      <div>
        {isLoading ? (
          <TableSkeleton
            columnCount={MISSIONS_TABLE_COLUMN_COUNT}
            rowCount={5}
          />
        ) : isError ? (
          <TableEmptyState
            title="Nenhuma missão encontrada"
            description="Não encontramos missões com os filtros selecionados."
          />
        ) : missions && missions.data.length > 0 ? (
          <>
            <Text
              variant="headingMd"
              tone="supporting"
              className={styles.headerLabel}
            >
              {missions.totalCount}{' '}
              {missions.totalCount === 1 ? 'missão' : 'missões'}
            </Text>
            {isStackedTableLayout ? (
              <Table.MobileList className={styles.missionsMobileList}>
                {missions.data.map(mission => renderMissionMobileCard(mission))}
              </Table.MobileList>
            ) : (
              <div className={styles.missionsTableScroll}>
                <Table className={styles.missionsTable}>
                  <Table.Header>
                    <Table.Row
                      gridTemplateColumns={MISSIONS_TABLE_GRID_COLUMNS}
                    >
                      <Table.HeaderCell>Missão</Table.HeaderCell>
                      <Table.HeaderCell>Data de início</Table.HeaderCell>
                      <Table.HeaderCell>Data de finalização</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {missions.data.map(mission => renderMissionRow(mission))}
                  </Table.Body>
                </Table>
              </div>
            )}
            <div className={styles.pagination}>
              <Pagination
                size="sm"
                limitPerPage={limit}
                currentPage={page}
                totalCount={missions.totalCount || 0}
                onPageChange={handleChangePage}
              />
            </div>
          </>
        ) : (
          <TableEmptyState
            title="Nenhuma missão encontrada"
            description="Não encontramos missões com os filtros selecionados."
          />
        )}
      </div>
    </div>
  );
};
